import type { AssistantReportDefinition } from '@triedge/domain-core';
import { resolveAssistantRouteFamily } from '../config/ai-assistant/route-family-registry';

export const DEFAULT_REPORT_GENERATION_PATHNAME = '/school/admissions/dashboard';

export type ReportGenerationRouteContext = {
  pathname: string;
  module: NonNullable<AssistantReportDefinition['module']>;
  routeFamily: NonNullable<AssistantReportDefinition['routeFamily']>;
  routeLabel: string;
  defaultTitle: string;
};

const normalizePathname = (value: string | null | undefined): string => {
  const normalized = String(value ?? '').trim();
  return normalized || DEFAULT_REPORT_GENERATION_PATHNAME;
};

const resolveReportGenerationRouteFamily = (pathname: string): {
  id: NonNullable<AssistantReportDefinition['routeFamily']>;
  module: NonNullable<AssistantReportDefinition['module']>;
  label: string;
} => {
  if (pathname === '/school/run-reports' || pathname.startsWith('/school/run-reports/')) {
    return {
      id: 'tuition',
      module: 'tuition',
      label: 'Tuition',
    };
  }

  const routeFamily = resolveAssistantRouteFamily(pathname);
  return {
    id: routeFamily.id as NonNullable<AssistantReportDefinition['routeFamily']>,
    module: routeFamily.module as NonNullable<AssistantReportDefinition['module']>,
    label: routeFamily.label,
  };
};

export const resolveReportGenerationRouteContext = (input?: {
  defaultTitle?: string | null;
  pathname?: string | null;
  module?: AssistantReportDefinition['module'] | null;
}): ReportGenerationRouteContext => {
  const pathname = normalizePathname(input?.pathname);
  const routeFamily = resolveReportGenerationRouteFamily(pathname);
  const defaultTitle = String(input?.defaultTitle ?? '').trim()
    || `${routeFamily.label} report builder`;

  return {
    pathname,
    module: (input?.module ?? routeFamily.module) as NonNullable<AssistantReportDefinition['module']>,
    routeFamily: routeFamily.id,
    routeLabel: routeFamily.label,
    defaultTitle,
  };
};

export const applyReportGenerationRouteContextToDefinition = (
  definition: AssistantReportDefinition,
  routeContext: ReportGenerationRouteContext,
): AssistantReportDefinition => ({
  ...definition,
  title: String(definition.title ?? '').trim() || routeContext.defaultTitle,
  pathname: routeContext.pathname,
  module: routeContext.module,
  routeFamily: routeContext.routeFamily,
});
